import moment from 'moment';

export function createCalendarComponents({
  currentView,
  formatHours,
  getDailyTotals,
  handleDayClick,
  handleRightClickEvent,
  setupLongPress,
  eventElementMapRef,
}) {
  function registerEventEl(el, event) {
    if (!el || !eventElementMapRef) return;
    const rbcEvent = el.closest('.rbc-event');
    if (rbcEvent) eventElementMapRef.current.set(rbcEvent, event);
  }
  return {
    toolbar: () => null,
    week: {
      header: ({ date, label }) => {
        const dayName = moment(date).format('dd');
        const dayNumber = moment(date).format('D');
        return (
          <div className="text-center">
            <div className="md:hidden">
              <div className="text-xs font-semibold text-white/80">{dayName}</div>
              <div className="text-sm font-bold">{dayNumber}</div>
            </div>
            <div className="hidden md:block">
              <div className="font-medium">{label}</div>
              {(currentView === 'week' || currentView === 'work_week' || currentView === 'three_day') && (
                <div className="text-xs text-white/60 mt-1 font-medium">
                  Total: {formatHours(getDailyTotals(date).total)} - Billable: {formatHours(getDailyTotals(date).billable)}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    work_week: {
      header: ({ date, label }) => {
        const dayName = moment(date).format('dd');
        const dayNumber = moment(date).format('D');
        return (
          <div className="text-center">
            <div className="md:hidden">
              <div className="text-xs font-semibold text-white/80">{dayName}</div>
              <div className="text-sm font-bold">{dayNumber}</div>
            </div>
            <div className="hidden md:block">
              <div className="font-medium">{label}</div>
              {(currentView === 'week' || currentView === 'work_week' || currentView === 'three_day') && (
                <div className="text-xs text-white/60 mt-1 font-medium">
                  Total: {formatHours(getDailyTotals(date).total)} - Billable: {formatHours(getDailyTotals(date).billable)}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    day: {
      header: ({ date, label }) => {
        const dayName = moment(date).format('dddd');
        const dayNumber = moment(date).format('D');
        const monthName = moment(date).format('MMM');
        return (
          <div className="text-center">
            <div className="md:hidden">
              <div className="text-xs font-semibold text-white/80">{dayName}</div>
              <div className="text-sm font-bold">{monthName} {dayNumber}</div>
            </div>
            <div className="hidden md:block">
              <div className="font-medium">{label}</div>
            </div>
          </div>
        );
      },
    },
    month: {
      dateCellWrapper: ({ value, children }) => {
        const date = value;
        const dayNumber = moment(date).format('D');
        const dailyTotals = getDailyTotals(date);
        const isToday = moment(date).isSame(moment(), 'day');
        return (
          <div 
            className={`w-full h-full cursor-pointer p-1 md:p-2 flex flex-col border-l border-[#273C61] ${isToday ? 'bg-[#FF7800]/10' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDayClick(date);
            }}
            title="Click to view day"
            style={{ minHeight: '100%', position: 'relative', zIndex: 12 }}
          >
            <div style={{ display: 'none' }}>{children}</div>
            <div className="flex-1 flex flex-col justify-center items-center text-center">
              <div className="text-sm md:text-lg font-bold mb-0.5 md:mb-1">{dayNumber}</div>
              <div className="text-xs space-y-0 md:space-y-0.5">
                <div className="font-medium text-white/70">
                  <span className="md:hidden">T: {formatHours(dailyTotals.total)}</span>
                  <span className="hidden md:inline">Total: {formatHours(dailyTotals.total)}</span>
                </div>
                <div className="font-medium text-white/70">
                  <span className="md:hidden">B: {formatHours(dailyTotals.billable)}</span>
                  <span className="hidden md:inline font-bold">Billable: {formatHours(dailyTotals.billable)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    event: ({ event }) => {
      // Don't render events in month view - only show date and hours
      if (currentView === 'month') return null;

      const durationMinutes = moment(event.end).diff(moment(event.start), 'minutes');
      const isShortEvent = durationMinutes <= 15;

      if (isShortEvent) {
        const startTime = moment(event.start).format('HH:mm');
        const endTime = moment(event.end).format('HH:mm');

        setTimeout(() => {
          const allEvents = document.querySelectorAll('.rbc-event');
          allEvents.forEach(el => {
            const customDiv = el.querySelector('[data-short-event="true"]');
            if (customDiv) {
              const label = el.querySelector('.rbc-event-label');
              if (label) {
                label.style.display = 'none';
                label.style.visibility = 'hidden';
                label.style.opacity = '0';
              }
              el.setAttribute('data-short-event', 'true');
            }
          });
        }, 0);

        return (
          <div
            ref={(el) => registerEventEl(el, event)}
            {...setupLongPress((e, syntheticEvent) => handleRightClickEvent(event, syntheticEvent))}
            style={{ height: '100%', position: 'relative', zIndex: 12 }}
            className="flex items-center justify-between w-full h-full px-2 py-1 min-h-[24px] cursor-pointer"
            data-short-event="true"
          >
            <div className="flex items-center min-w-0 flex-1">
              <span className="text-[9px] font-medium text-white/70 whitespace-nowrap mr-1">
                {startTime}-{endTime}
              </span>
              <span className="text-xs font-semibold truncate">
                {event.title.length > 20 ? `${event.title.slice(0, 20)}...` : event.title}
              </span>
            </div>
            {(event.type === 'Udvikling' || event.type === 'Workshop') && (
              <img
                src={
                  event.billingtype === 'Billable'
                    ? '/stateIcons/money.png'
                    : event.billingtype === 'Pro Bono'
                    ? '/stateIcons/sustainability.png'
                    : '/stateIcons/workplace.png'
                }
                alt={event.billingtype}
                className="w-4 h-4 flex-shrink-0 ml-2 brightness-0 invert"
              />
            )}
          </div>
        );
      }

      // Normal layout
      return (
        <div
          ref={(el) => registerEventEl(el, event)}
          {...setupLongPress((e, syntheticEvent) => handleRightClickEvent(event, syntheticEvent))}
          style={{ height: '100%' }}
          className="flex flex-col h-full"
        >
          <div className="flex items-center mb-1 justify-between w-full">
            <strong className="text-[13px] font-bold leading-tight">
              {event.title.length > 35 ? `${event.title.slice(0, 35)}...` : event.title}
            </strong>
            {(event.type === 'Udvikling' || event.type === 'Workshop') && (
              <img
                src={
                  event.billingtype === 'Billable'
                    ? '/stateIcons/money.png'
                    : event.billingtype === 'Pro Bono'
                    ? '/stateIcons/sustainability.png'
                    : '/stateIcons/workplace.png'
                }
                alt={event.billingtype}
                className="w-6 h-6 inline-block ml-1 brightness-0 invert"
              />
            )}
          </div>
          <small className="text-[11px] leading-tight text-white/80">{event.timecodeName}</small>
          <small className="text-[11px] leading-tight text-white/80">{event.projectName}</small>
        </div>
      );
    },
  };
}